import { useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type ColumnDef } from '@tanstack/react-table'
import { CalendarX2, Code2, ImageIcon } from 'lucide-react'
import { toast } from 'sonner'
import { CreativePreviewDialog } from './CreativePreviewDialog'
import { previewSize } from './CreativeLivePreviewForm'
import { PreviewApi } from '@/services/creatives/previewApi'
import { AssetHoverPreview } from './AssetHoverPreview'
import { Badge } from '@/components/ui/badge'
import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import type { ListSort } from '@/services/core/paging'
import { DateCell, MoneyCell, MutedCell, NameCell, NumberCell, StatusChip } from '@/components/TableCells'
import { InlineEditBar } from '@/components/InlineEditBar'
import { useInlineSave } from '@/hooks/useInlineSave'
import type { InlineEditSpec } from '@/lib/inlineEdit'
import { Creative, CreativesApi, type CreativeBulkInput } from '@/services/creatives/creativesApi'
import { resolveFeeCurrency } from '@/services/earnings/fees'
import { AssetsApi, isHtmlAsset, isVideoAsset } from '@/services/assets/assetsApi'
import { LayoutsApi } from '@/services/sites/layoutsApi'
import { useCurrentUser } from '../contexts/UserContext'
import { isSuperAdmin } from '../data/superAdmins'
import { statMetricColumns } from './statsColumns'
import { DEFAULT_HTML_SIZE, ScaledHtmlFrame } from './HtmlPreviewThumbnail'
import { useStatLabel } from '../hooks/useStatLabel'
import { CREATIVE_STAT_METRICS, type CreativeStatsRow } from '../hooks/useCreativeStats'
import { TargetingListCell } from '@/components/targeting/TargetingListCell'
import { useNetwork } from '../contexts/NetworkContext'
import { CreativeStateSelect, type CreativeState } from './CreativeStateSelect'
import { apiErrorDescription } from '@/services/core/apiError'
import { CREATIVE_LINE_ITEM_ENDED } from '@/services/core/validationWarnings'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { warningText } from './ValidationWarnings'
import { cn } from '@/lib/utils'

/** The warning saying the creative's line item has ended, if it has. */
function lineItemEnded(creative: Creative) {
  return creative.validationWarnings.find((w) => w.code === CREATIVE_LINE_ITEM_ENDED)
}

/** Fixed 16:9 frame so every row's preview occupies the same space. */
function PreviewFrame({ children, muted }: { children: ReactNode; muted?: boolean }) {
  return (
    <div
      className={cn(
        'flex aspect-video w-28 items-center justify-center overflow-hidden rounded border bg-muted p-1 transition',
        // A creative that no longer serves fades back until hovered.
        muted && 'opacity-50 grayscale hover:opacity-100 hover:grayscale-0',
      )}
    >
      {children}
    </div>
  )
}

function VideoPreview({ src }: { src: string }) {
  const ref = useRef<HTMLVideoElement>(null)
  return (
    <video
      ref={ref}
      src={src}
      className={previewMediaClass}
      muted
      playsInline
      preload="metadata"
      loop
      onMouseEnter={() => ref.current?.play()}
      onMouseLeave={() => { if (ref.current) { ref.current.pause(); ref.current.currentTime = 0 } }}
    />
  )
}

export interface CreativeAssetPreview {
  src: string
  mimeType: string
  /** Shown in the hover preview alongside the dimensions. */
  fileSize?: number
  width?: number
  height?: number
}

const previewMediaClass = 'block max-h-full max-w-full object-contain'

function CreativePreviewCell({
  creative,
  preview,
  isThirdParty,
}: {
  creative: Creative
  preview?: CreativeAssetPreview
  /** Third-party creatives carry ad code rather than an asset, so they get a code icon. */
  isThirdParty?: boolean
}) {
  const ended = !!lineItemEnded(creative)
  // Fallback: fetch assets if no preview was resolved externally
  const { data: assets = [] } = useQuery({
    queryKey: ['creative-assets-preview', creative.id],
    queryFn: () => AssetsApi.getByCreative(creative.id),
    staleTime: 5 * 60 * 1000,
    enabled: !preview && !isThirdParty,
  })

  const resolvedPreview: CreativeAssetPreview | null = preview ?? (() => {
    const selectedAssetId = Object.values(creative.layoutParameters ?? {})
      .filter((v): v is { id: string } => !!v && typeof v === 'object' && 'id' in v)
      .map((v) => v.id)[0] ?? null
    const asset = selectedAssetId
      ? (assets.find((a) => a.id === selectedAssetId) ?? assets[0])
      : assets[0]
    if (!asset) return null
    const isVideo = isVideoAsset(asset.mimeType ?? '')
    const src = asset.previewCdnId || (isVideo ? asset.cdnId : asset.cdnId)
    return src
      ? {
          src,
          mimeType: asset.mimeType,
          fileSize: asset.fileSize,
          width: asset.width,
          height: asset.height,
        }
      : null
  })()

  if (!resolvedPreview) {
    return (
      <PreviewFrame muted={ended}>
        {isThirdParty ? (
          <Code2 className="size-4 opacity-35" />
        ) : (
          <ImageIcon className="size-4 opacity-35" />
        )}
      </PreviewFrame>
    )
  }

  // Hovering enlarges the asset — a 112px thumbnail is too small to judge by
  return (
    <AssetHoverPreview
      src={resolvedPreview.src}
      mimeType={resolvedPreview.mimeType}
      caption={creative.name}
      fileSize={resolvedPreview.fileSize}
      width={resolvedPreview.width}
      height={resolvedPreview.height}
    >
      <PreviewFrame muted={ended}>
        {isVideoAsset(resolvedPreview.mimeType) ? (
          <VideoPreview src={resolvedPreview.src} />
        ) : isHtmlAsset(resolvedPreview.mimeType) ? (
          <ScaledHtmlFrame
            src={resolvedPreview.src}
            width={resolvedPreview.width || DEFAULT_HTML_SIZE.width}
            height={resolvedPreview.height || DEFAULT_HTML_SIZE.height}
            maxWidth={104}
            maxHeight={55}
            interactive={false}
          />
        ) : (
          <img src={resolvedPreview.src} alt="" className={previewMediaClass} />
        )}
      </PreviewFrame>
    </AssetHoverPreview>
  )
}

/**
 * A creative's state in a list row, picked from the same badge menu the
 * creative page uses and saved as soon as it is picked.
 */
function CreativeStateCell({ creative }: { creative: Creative }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (userState: CreativeState) =>
      CreativesApi.update(creative.id, { name: creative.name, userState }),
    onSuccess: (saved) => {
      queryClient.setQueryData(['creative', creative.id], saved)
      for (const key of ['creatives', 'advertiser-creatives', 'lineitem-creatives']) {
        queryClient.invalidateQueries({ queryKey: [key] })
      }
      toast.success(t('common.saved'), { duration: 3000 })
    },
    onError: (error) => {
      toast.error(t('common.error'), { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  const ended = lineItemEnded(creative)

  return (
    // The row navigates on click; picking a state, in the menu too, should not.
    <span className="flex flex-col items-start gap-1" onClick={(e) => e.stopPropagation()}>
      <CreativeStateSelect
        value={mutation.isPending ? mutation.variables : creative.userState}
        onChange={mutation.isPending ? undefined : (state) => {
          if (state !== creative.userState) mutation.mutate(state)
        }}
      />
      {/* Approved or not, nothing serves once the line item is over. */}
      {ended && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              className="inline-flex items-center gap-1 rounded-sm pl-1 text-[11px] text-(--ad-text-faint)"
            >
              <CalendarX2 className="size-3" />
              {t('creatives.lineItemEnded')}
            </span>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-xs">
            {warningText(ended)}
          </TooltipContent>
        </Tooltip>
      )}
    </span>
  )
}

// ─── Editable fields ───────────────────────────────────────────────────────

/**
 * What each editable column of the list may change, and the payload that
 * changes it. Size is offered as two plain numbers: what a creative may be is
 * its layout's business, and the server enforces that on the save.
 */
function useCreativeEditSpecs() {
  const { t } = useTranslation()

  return useMemo(() => {
    const dimension = (field: 'width' | 'height'): InlineEditSpec<Creative> => ({
      type: 'number',
      min: 1,
      value: (creative) => creative[field] || null,
      patch: (value) => ({ [field]: value === null ? 0 : Number(value) }),
    })

    const specs: Record<string, InlineEditSpec<Creative>> = {
      name: {
        type: 'text',
        maxLength: 255,
        value: (creative) => creative.name || null,
        patch: (value) => ({ name: value === null ? '' : String(value) }),
        validate: (value) =>
          String(value ?? '').trim() ? undefined : t('common.nameRequired'),
      },
      objectState: {
        type: 'select',
        value: (creative) => creative.objectState || null,
        options: () => [
          { value: 'ACTIVE', label: t('common.active') },
          { value: 'INACTIVE', label: t('common.inactive') },
        ],
        patch: (value) => ({ objectState: String(value) }),
      },
      width: dimension('width'),
      height: dimension('height'),
    }
    return specs
  }, [t])
}

/** The API field each sortable column orders by when the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  advertiserName: 'advertiser.name',
  lineItemName: 'lineItem.name',
  layout: 'layout.name',
  type: 'type',
  userState: 'userState',
  objectState: 'objectState',
  width: 'width',
  height: 'height',
  updateTime: 'updateTime',
  createTime: 'createTime',
}

interface CreativeTableProps {
  creatives: Creative[]
  loading?: boolean
  emptyMessage?: string
  showAdvertiser?: boolean
  showPreview?: boolean
  /** Map of creativeId -> resolved asset preview, skips per-creative asset fetch when provided */
  previewAssets?: Record<string, CreativeAssetPreview>
  selection?: Creative[]
  onSelectionChange?: (items: Creative[]) => void
  /** Delivery metrics per creative id — adds the stats columns when given. */
  stats?: Record<string, CreativeStatsRow>
  /**
   * Key the column choice is remembered under. Give a table with extra columns
   * its own key so it does not share the creatives list's picked columns.
   */
  columnsKey?: string
  /** Offers the fields of the list for editing in place. On by default. */
  editable?: boolean
  /**
   * Previews open the ad server's own preview window — the creative page's
   * "open on blank page" — instead of the preview dialog.
   */
  previewOnBlankPage?: boolean
  /** Sorts on the server — pass both for a list the server pages. */
  sort?: ListSort | null
  onSortChange?: (sort: ListSort | null) => void
}

export function CreativeTable({
  creatives,
  loading,
  emptyMessage,
  showAdvertiser = true,
  showPreview = false,
  previewAssets,
  selection,
  onSelectionChange,
  stats,
  columnsKey = 'creatives',
  editable = true,
  previewOnBlankPage = false,
  sort,
  onSortChange,
}: CreativeTableProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { currentNetwork } = useNetwork()
  const primaryTagId = currentNetwork?.tagId
  const statLabel = useStatLabel()
  const selectable = !!onSelectionChange
  const currentUser = useCurrentUser()
  const isAdmin = isSuperAdmin(currentUser?.username)
  const [previewCreative, setPreviewCreative] = useState<Creative | null>(null)

  /** The ad server's preview page for a saved creative, in its own window. */
  const openBlankPagePreview = (creative: Creative) => {
    const { width, height } = previewSize(creative)
    PreviewApi.openWindow(
      PreviewApi.blankPageUrl(creative.id, {
        lineItemId: creative.lineItemId || undefined,
        width,
        height,
        name: creative.name,
      })
    )
  }

  const specs = useCreativeEditSpecs()
  const { edits, setEdit, bar } = useInlineSave<Creative>({
    save: (rows) =>
      CreativesApi.updateMany(rows.map(({ id, patch }) => ({ id, ...patch }) as CreativeBulkInput)),
    invalidateKeys: ['creatives', 'advertiser-creatives', 'lineitem-creatives', 'creative'],
    errorTitle: t('creatives.saveFailed'),
  })

  // Creative payloads only carry a layout reference, so resolve the type from the layout list
  const { data: layouts = [] } = useQuery({
    queryKey: ['layouts'],
    queryFn: () => LayoutsApi.getAll(),
    staleTime: 5 * 60 * 1000,
    enabled: showPreview,
  })
  const thirdPartyLayoutIds = useMemo(
    () => new Set(layouts.filter((l) => l.layoutType === 'THIRD_PARTY').map((l) => l.id)),
    [layouts]
  )

  const columns = useMemo<ColumnDef<Creative, unknown>[]>(() => [
    ...(selectable ? [selectionColumn<Creative>()] : []),
    {
      accessorKey: 'userState',
      header: t('userStates.label'),
      size: 130,
      cell: ({ row }) => <CreativeStateCell creative={row.original} />,
    },
    ...(showPreview ? [{
      id: 'preview',
      header: '',
      size: 128,
      // An image only — and loading one for every row of a download is costly.
      meta: { label: t('creatives.preview'), export: false as const },
      cell: ({ row }: { row: { original: Creative } }) => (
        <button
          type="button"
          title={t('creatives.preview')}
          className="cursor-pointer rounded transition-opacity hover:opacity-80"
          onClick={(e) => {
            e.stopPropagation()
            if (previewOnBlankPage) {
              openBlankPagePreview(row.original)
            } else {
              setPreviewCreative(row.original)
            }
          }}
        >
          <CreativePreviewCell
            creative={row.original}
            preview={previewAssets?.[row.original.id]}
            isThirdParty={thirdPartyLayoutIds.has(row.original.layoutId)}
          />
        </button>
      ),
    }] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 200, edit: specs.name },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    ...(showAdvertiser ? [{
      accessorKey: 'advertiserName',
      header: t('common.advertiser'),
      meta: { minWidth: 140 },
      cell: ({ row }: { row: { original: Creative } }) => <MutedCell value={row.original.advertiserName} />,
    }] : []),
    {
      accessorKey: 'lineItemName',
      header: t('lineItems.lineItem'),
      meta: { minWidth: 140 },
      cell: ({ row }) => <MutedCell value={row.original.lineItemName} />,
    },
    {
      id: 'size',
      header: t('creatives.size'),
      size: 100,
      cell: ({ row }) => (
        <span className="text-xs tabular-nums text-(--ad-text)">
          {row.original.width && row.original.height
            ? `${row.original.width}×${row.original.height}`
            : '—'}
        </span>
      ),
    },
    {
      id: 'layout',
      header: t('creatives.layout'),
      meta: { minWidth: 120 },
      cell: ({ row }) => {
        const { layoutId, layoutName } = row.original
        if (!layoutName) return <MutedCell />
        if (isAdmin && layoutId) {
          return (
            <Badge
              variant="secondary"
              className="cursor-pointer text-[11px]"
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/admin/layouts/${layoutId}`)
              }}
            >
              {layoutName}
            </Badge>
          )
        }
        return <Badge variant="outline" className="text-[11px]">{layoutName}</Badge>
      },
    },
    {
      accessorKey: 'type',
      header: t('common.type'),
      size: 120,
      cell: ({ row }) => <MutedCell value={row.original.type} />,
    },
    {
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 130,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    },
    {
      id: 'targeting',
      header: t('targeting.title'),
      size: 130,
      enableSorting: false,
      cell: ({ row }) => (
        <TargetingListCell
          queryKey={['creative', row.original.id]}
          queryFn={() => CreativesApi.getById(row.original.id)}
          targeting={row.original.targetingComplete ? row.original.targeting : undefined}
          save={(targeting) =>
            CreativesApi.update(row.original.id, { name: row.original.name, targeting })
          }
        />
      ),
    },
    optionalColumn<Creative>({
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      meta: { edit: specs.objectState },
      cell: ({ row }) => <StatusChip state={row.original.objectState} />,
    }),
    optionalColumn<Creative>({
      accessorKey: 'width',
      header: t('creatives.width'),
      size: 100,
      meta: { edit: specs.width },
      cell: ({ row }) => <NumberCell value={row.original.width ?? null} />,
    }),
    optionalColumn<Creative>({
      accessorKey: 'height',
      header: t('creatives.height'),
      size: 100,
      meta: { edit: specs.height },
      cell: ({ row }) => <NumberCell value={row.original.height ?? null} />,
    }),
    optionalColumn<Creative>({
      id: 'creativeFormatFee',
      header: t('fees.creativeFormatFee'),
      size: 130,
      cell: ({ row }) => {
        const fee = row.original.creativeFormatFee
        if (!fee) return <MutedCell />
        if (fee.type === 'PERCENTAGE') return <MutedCell value={`${fee.percentage ?? 0}%`} />
        // The table spans line items, so there is no one bid currency to match
        // a multi-currency fee against — show the first amount it quotes.
        return <MoneyCell value={resolveFeeCurrency(fee, undefined).monetary ?? null} fractionDigits={4} />
      },
    }),
    optionalColumn<Creative>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
    ...(stats
      ? statMetricColumns<Creative>({
          metrics: CREATIVE_STAT_METRICS,
          statsFor: (row) => stats[row.id],
          label: statLabel,
        })
      : []),
  ], [t, statLabel, selectable, showAdvertiser, showPreview, previewAssets, thirdPartyLayoutIds, isAdmin, navigate, stats, specs, previewOnBlankPage, primaryTagId])

  return (
    <>
      <DataTable
        columns={columns}
        data={creatives}
        loading={loading}
        emptyMessage={emptyMessage ?? t('creatives.noCreativesFound')}
        columnsKey={columnsKey}
        rowHref={(row) => `/advertising/creatives/${row.id}`}
        selection={selection}
        onSelectionChange={onSelectionChange}
        edits={editable ? edits : undefined}
        onEditChange={editable ? setEdit : undefined}
        serverSort={
          onSortChange && { sort: sort ?? null, onChange: onSortChange, fields: SORT_FIELDS }
        }
      />
      <InlineEditBar {...bar} />
      <CreativePreviewDialog
        creative={previewCreative}
        open={!!previewCreative}
        onOpenChange={(open) => { if (!open) setPreviewCreative(null) }}
      />
    </>
  )
}
